/**
 * KnowledgeAtlasExplorer — Knowledge Atlas M5 UI (Knowledge Controls tab).
 *
 * Design doc §5:
 *   "The Knowledge Atlas is a UI surface inside Knowledge Controls with
 *    four primary views, all sharing a common shell (header, left-rail
 *    navigation, and an audit-visibility banner at the bottom):
 *      · Graph View     – nodes = entities, edges = AI-drawn links
 *      · Entity View    – accumulated summary + key themes + deltas
 *      · Delta Feed     – chronological list of Knowledge Atlas commits
 *      · Meta-query bar – prompt bar for pattern-mining questions"
 *
 * M5 wires the shell + four views against the live M3/M4 endpoints.
 * Meta-query is a mocked prompt (real META routing is M6).
 *
 * Every interactive element carries a data-testid attribute.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import authedAxios from '../../lib/authedAxios';
import { toast } from 'sonner';
import {
  Network, Sparkles, RefreshCw,
  Loader2, AlertCircle, FileText,
} from 'lucide-react';

import { AtlasScaleView, topicDisplayName }     from './AtlasScaleView';
import { AtlasEntityView }    from './AtlasEntityView';
import { AtlasDocuments }     from './AtlasDocuments';
import { AtlasIntelligenceBadge } from './AtlasIntelligenceBadge';
// AtlasMemoryEconomyBadge temporarily hidden — see comment near render site
// where <AtlasMemoryEconomyBadge topic={tenant} /> is commented out.
// import { AtlasMemoryEconomyBadge } from './AtlasMemoryEconomyBadge';
import { AtlasConceptFinder } from './AtlasConceptFinder';
import { HoverTip } from '../HoverTip';
import { trackEvent } from '../../utils/telemetry';

const API_ROOT = process.env.REACT_APP_BACKEND_URL;

// IJ-070 hotfix (2026-07-23) — every /api/ka/* endpoint now requires a
// Bearer token (auth-hardening landed in the July-23 deploy). The shared
// `authedAxios` instance (in /app/frontend/src/lib/authedAxios.js) attaches
// the token from localStorage on every request. All axios calls in this
// file MUST use `authedAxios` (not the bare `axios` import) so no future
// KA call misses the header again.

const VIEWS = [
  {
    key: 'graph',
    label: 'Map',
    Icon: Network,
    tooltip: 'A live map of every Concept and how it\'s connected. Node size = importance, colour = recency.',
  },
  {
    key: 'entity',
    label: 'Concept Details',
    Icon: Sparkles,
    tooltip: 'Everything the graph knows about one Concept — sources, Connections, contradictions, and its short summary.',
  },
  {
    key: 'documents',
    label: 'Documents',
    Icon: FileText,
    tooltip: 'Every artefact uploaded to this Topic — most-recent first, with a NEW badge on anything uploaded in the last 24hrs.',
  },
];
// NOTE: The 4th "What Changed" (key='delta') tab was removed per D9 Cluster 2
// ruling (2026-02-08) — cryptographic delta-feed surface was design-doc bleed-
// through with no D-directive backing. Backend delta pipeline + /api/atlas/deltas
// route remain intact; only the visible tab is removed. AtlasDeltaFeed component
// preserved in /components/KnowledgeAtlas/ for potential future re-use.

// Format an age label from an ISO date, e.g. "3 h ago", "just now",
// "2 d ago". Used for the "My uploads" panel + Impact Report hero.
const formatAgeLabel = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
};

export const KnowledgeAtlasExplorer = forwardRef((props, ref) => {
  const [tenants, setTenants]           = useState([]);
  const [tenant, setTenant]             = useState(null);
  const [compareTenant, setCompareTenant] = useState(null);
  const [atlasEntities, setAtlasEntities] = useState([]);
  const [reviewQueue, setReviewQueue]   = useState([]);
  const [documents, setDocuments]       = useState([]);
  const [sharedSubTopics, setSharedSubTopics] = useState([]);
  const [secondarySubTopicCounts, setSecondarySubTopicCounts] = useState({});
  // KG-upload wiring (operator D6 directive 2026-02-07): allow uploading
  // documents directly into the Atlas's current Topic — two triggers, one
  // hidden file input. Auto-partitions to the currently-selected tenant.
  const kgUploadInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const triggerKgUpload = () => {
    // SPD v5.75-fix (2026-07-11) — admin gate parity with KG page.
    // Non-admin users see the same "restricted to administrators" toast
    // as they do on the main KG page, so the two upload icons behave
    // identically. Backend `_ensure_admin` remains the source of truth.
    if (!isAdmin) {
      toast.info('File upload is restricted to administrators.', { duration: 4500 });
      return;
    }
    if (!tenant) {
      toast.error('Select a Topic first.');
      return;
    }
    if (kgUploadInputRef.current) {
      kgUploadInputRef.current.value = ''; // allow re-selecting the same file
      kgUploadInputRef.current.click();
    }
  };

  // Expose the upload trigger to the parent page so the top-right
  // KG-icon in the page header can share this single file picker.
  useImperativeHandle(ref, () => ({
    triggerKgUpload,
  }), [tenant]);
  const [subTopicGraph, setSubTopicGraph] = useState(null);
  // `newSince` — ISO timestamp used to flag Sub-Topics and Pathways as
  // "new" on the Neuropathway View. Persisted per-tenant in
  // sessionStorage so it survives page refresh but dies with the tab
  // (matches operator direction 2026-07-06: "…until user clears the
  // page or starts a new search or closes the App").
  const [newSince, setNewSince] = useState(null);
  const [view, setView]                 = useState('graph');
  // SPD v5.73 (2026-02-11) — Map tab reset token. Iteration D14 bug: when
  // the operator toggled the SUB-TOPICS-LIST sub-mode inside the Map tab
  // and then clicked the top-level Map tab expecting the bubbles view
  // back, nothing happened because AtlasScaleView holds its own viewMode
  // state and the top-level tab was already `graph` (no re-mount). We
  // increment this token every time the Map tab is clicked; AtlasScaleView
  // watches it and resets to the Connection (bubbles) sub-view.
  const [mapResetToken, setMapResetToken] = useState(0);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [subtopicFilter, setSubtopicFilter]     = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  // SPD v5.90 (2026-02-16) — partial-reload warning distinct from a hard error.
  // Previously `reloadTenantState` used Promise.all(), so a single 520/504 on
  // any of the 3 slice endpoints (entities, review-queue, documents) would
  // fire the red error banner and blank the whole tenant view — even when
  // the graph itself was still perfectly usable from the last successful
  // fetch. Operator caught this on Prod after ingesting a large deck pushed
  // /api/ka/entities over Cloudflare's origin-response threshold (HTTP 520).
  // The banner now downgrades to amber when SOME slices succeeded, and only
  // stays red if ALL three slices failed.
  const [partialWarning, setPartialWarning] = useState(null);
  // Admin gate for the audit-visibility banner. Phase 2 D2_KA feedback:
  // the audit rail is an operator/admin surface — hide it for regular
  // users. Resolved once on mount via /api/auth/verify (same pattern
  // used by App.js for the KG upload gate).
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    try {
      const token = window.localStorage.getItem('startech_token');
      if (!token) return;
      authedAxios.get(`${API_ROOT}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => { if (r.data && typeof r.data.is_admin === 'boolean') setIsAdmin(r.data.is_admin); })
        .catch(() => { /* keep default false */ });
    } catch (_) { /* localStorage unavailable — non-admin */ }
  }, []);

  // IJ-339 (2026-08-18) · Tenant discovery via the new /ka/tenants
  // endpoint. Bypasses the 500-row cap on /ka/entities that previously
  // hid a new tenant whose entities landed past position 500 in the
  // natural insert order. Defensive fallback to the old union-based
  // path retained in case the endpoint is not deployed yet.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        let list = null;
        try {
          const r = await authedAxios.get(`${API_ROOT}/api/ka/tenants`);
          list = Array.from(new Set(r.data?.tenants || [])).sort();
        } catch (endpointErr) {
          // Fallback: derive from atlas_entities + delta_reports.
          const [wRes, dRes] = await Promise.all([
            authedAxios.get(`${API_ROOT}/api/ka/entities?limit=500`),
            authedAxios.get(`${API_ROOT}/api/ka/delta-reports?limit=200`),
          ]);
          const set = new Set();
          (wRes.data?.entities || []).forEach((e) => e.tenant && set.add(e.tenant));
          (dRes.data?.reports  || []).forEach((r) => r.tenant && set.add(r.tenant));
          list = Array.from(set).sort();
        }
        if (!alive) return;
        setTenants(list);
        if (list.length && !tenant) setTenant(list[0]);
      } catch (e) {
        setError(`Failed to load Knowledge Atlas state: ${e.message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Reload per-tenant state whenever tenant changes.
  const reloadTenantState = async (t) => {
    if (!t) {
      setAtlasEntities([]);
      setReviewQueue([]);
      setDocuments([]);
      setSharedSubTopics([]);
      setSecondarySubTopicCounts({});
      setPartialWarning(null);
      setError(null);
      return;
    }
    // Fresh notice slate for this reload; whichever level we end on wins.
    setPartialWarning(null);
    setError(null);
    try {
      setLoading(true);
      // SPD v5.90 (2026-02-16) — Promise.allSettled so a single 520/504 on
      // any one slice endpoint no longer nukes the other two slices. The
      // graph relies on `atlasEntities` and stays visible; review-queue &
      // documents populate independently.
      const [wRes, qRes, docRes] = await Promise.allSettled([
        authedAxios.get(`${API_ROOT}/api/ka/entities?tenant=${encodeURIComponent(t)}&limit=500`,     { timeout: 60000 }),
        authedAxios.get(`${API_ROOT}/api/ka/review-queue?tenant=${encodeURIComponent(t)}&limit=200`, { timeout: 60000 }),
        authedAxios.get(`${API_ROOT}/api/ka/documents?tenant=${encodeURIComponent(t)}`,              { timeout: 60000 }),
      ]);

      // Populate whichever slices resolved. Skip the ones that rejected so
      // we do NOT clobber last-known-good state with an empty array.
      if (wRes.status === 'fulfilled') {
        setAtlasEntities(wRes.value.data?.entities || []);
      }
      if (qRes.status === 'fulfilled') {
        setReviewQueue(qRes.value.data?.items || []);
      }
      if (docRes.status === 'fulfilled') {
        // Normalise document shape for the scale view: `doc_id` (matches
        // entity `source_documents`), a human age label, and a concept
        // count for the "My uploads" hover meta.
        const rawDocs = docRes.value.data?.documents || [];
        setDocuments(rawDocs.map((d) => ({
          ...d,
          doc_id: d.id,
          age_label: formatAgeLabel(d.upload_date),
          concept_count: d.entities_extracted || 0,
        })));
      }

      // Build the appropriate notice level.
      const failedSlices = [];
      if (wRes.status   === 'rejected') failedSlices.push({ name: 'concept graph', err: wRes.reason });
      if (qRes.status   === 'rejected') failedSlices.push({ name: 'review queue',  err: qRes.reason });
      if (docRes.status === 'rejected') failedSlices.push({ name: 'documents',     err: docRes.reason });

      if (failedSlices.length === 3) {
        // Total failure — hard red banner.
        const first = failedSlices[0].err;
        setError(
          `Failed to load Topic ‘${topicDisplayName(t)}’: ${first?.message || 'network error'}`,
        );
      } else if (failedSlices.length > 0) {
        // Partial failure — amber warning, showing last-known state.
        const names = failedSlices.map((s) => s.name).join(', ');
        setPartialWarning(
          `Some data for Topic ‘${topicDisplayName(t)}’ could not be refreshed (${names}). Showing last-known state.`,
        );
      }
    } catch (e) {
      // Belt-and-braces — Promise.allSettled itself should never throw,
      // but if `setLoading` or one of the setState calls does, still show
      // a friendly banner.
      setError(`Failed to load Topic ‘${topicDisplayName(t)}’: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadTenantState(tenant); }, [tenant]);

  // SPD v5.71 — atlas_visit telemetry. One event per (tenant, session) so we
  // measure how many people actually enter the Knowledge Atlas surface. Uses
  // sessionStorage as the dedupe key so a route-hop back to the KA in the
  // same session does not double-count.
  useEffect(() => {
    if (!tenant) return;
    try {
      const dedupeKey = `atlas_visit_${tenant}`;
      if (window.sessionStorage.getItem(dedupeKey)) return;
      window.sessionStorage.setItem(dedupeKey, Date.now().toString());
      trackEvent('atlas_visit', { tenant: String(tenant).slice(0, 64) });
    } catch (_) { /* sessionStorage disabled — silent noop */ }
  }, [tenant]);

  // ─── newSince — server-authoritative marker (SPD v5.68) ─────────────
  // Reads the last-refresh timestamp from /api/ka/refresh-marker, which
  // is set by the nightly APScheduler job at 02:00 America/Toronto. We
  // no longer read from sessionStorage — the server value is the source
  // of truth so every viewer sees the same "since last refresh" window.
  useEffect(() => {
    if (!tenant) { setNewSince(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await authedAxios.get(
          `${API_ROOT}/api/ka/refresh-marker?tenant=${encodeURIComponent(tenant)}`,
        );
        if (alive) setNewSince(r?.data?.last_refresh_at || null);
      } catch (_) {
        if (alive) setNewSince(null);
      }
    })();
    return () => { alive = false; };
  }, [tenant]);

  const persistNewSince = (t, iso) => {
    // Retained for the Refresh button in the header — a manual advance
    // hits the server marker via POST is out of scope for this SPD, so
    // for now we just update local state and let the next tenant switch
    // reload from the server.
    setNewSince(iso || null);
    // Best-effort cache in sessionStorage so a page refresh doesn't lose
    // the manual mark before the next fetch completes.
    try {
      const key = `ka_new_since_${t}`;
      if (iso) window.sessionStorage.setItem(key, iso);
      else window.sessionStorage.removeItem(key);
    } catch (_) { /* private mode — silent noop */ }
  };

  const markReindexed = (isoSince) => {
    if (!tenant) return;
    const iso = isoSince || new Date().toISOString();
    persistNewSince(tenant, iso);
    setNewSince(iso);
  };

  const clearNewSince = () => {
    if (tenant) persistNewSince(tenant, null);
    setNewSince(null);
  };

  // KG upload handler — file(s) → POST /api/documents/upload with the
  // current tenant as `partition`. Refreshes the Atlas state on success
  // so the new artefact appears in "My uploads" and the Concepts count
  // ticks up (once the async reindex completes). Operator D6 · 2026-02-07.
  const handleKgUploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!tenant) {
      toast.error('Select a Topic first.');
      return;
    }
    const files = Array.from(fileList);
    setIsUploading(true);
    let ok = 0, failed = 0;
    const token = window.localStorage.getItem('startech_token') || '';
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // Sequential for now — matches the main KG page batch flow's cadence
    // and keeps LLM extraction from thrashing. Parallelise in a later pass.
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('partition', tenant);
      try {
        await authedAxios.post(`${API_ROOT}/api/documents/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          timeout: 180_000,
        });
        ok += 1;
      } catch (e) {
        failed += 1;
        const msg = e?.response?.data?.detail || e.message || 'Upload failed';
        toast.error(`${file.name}: ${msg}`);
      }
    }
    setIsUploading(false);
    if (ok > 0) {
      toast.success(
        `${ok} document${ok === 1 ? '' : 's'} uploaded to ${topicDisplayName(tenant)}.` +
        (failed ? ` ${failed} failed.` : ''),
      );
      // Refresh so the new upload lands in My uploads + the atlas counts
      // update once the async reindex settles.
      reloadTenantState(tenant);
    }
  };

  // Compare-topic change also invalidates the "new since" marker for
  // the previous compare — it belonged to a different comparison
  // context and shouldn't leak.
  useEffect(() => {
    // no-op; the primary useEffect above handles the tenant-scoped
    // read. Kept as an anchor for future compare-scoped highlighting.
  }, [compareTenant]);

  // ─── Sub-Topic graph fetch (Neuropathway View data) ──────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tenant) { if (alive) setSubTopicGraph(null); return; }
      try {
        const params = new URLSearchParams({ topic: tenant });
        if (compareTenant) params.set('compare_with', compareTenant);
        if (newSince)      params.set('since',        newSince);
        const r = await authedAxios.get(
          `${API_ROOT}/api/atlas/sub-topic-graph?${params.toString()}`
        );
        if (!alive) return;
        setSubTopicGraph(r.data);
      } catch (e) {
        if (alive) setError(`Sub-Topic graph fetch failed: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, [tenant, compareTenant, newSince]);

  // Multi-Topic overlay — when `compareTenant` is set, fetch the
  // union endpoint to derive the shared Sub-Topic set. Primary
  // orbs stay Iceberg blue; shared Sub-Topics recolour to Amber.
  // (Mockup v0.6, operator-approved 2026-07-06.)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tenant || !compareTenant || compareTenant === tenant) {
        if (alive) {
          setSharedSubTopics([]);
          setSecondarySubTopicCounts({});
        }
        return;
      }
      try {
        const r = await authedAxios.get(
          `${API_ROOT}/api/atlas/multi-topic`
          + `?topic=${encodeURIComponent(tenant)}`
          + `&compare_with=${encodeURIComponent(compareTenant)}`
        );
        if (!alive) return;
        setSharedSubTopics(r.data?.shared_sub_topics || []);
        setSecondarySubTopicCounts(r.data?.secondary_sub_topic_counts || {});
      } catch (e) {
        if (alive) setError(`Multi-Topic overlay failed: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, [tenant, compareTenant]);

  const selectedEntity = useMemo(
    () => atlasEntities.find((e) => e.entity_id === selectedEntityId) || null,
    [atlasEntities, selectedEntityId]
  );

  const isEmpty = atlasEntities.length === 0;

  return (
    <div data-testid="atlas-explorer-root" className="relative z-20 min-h-[70vh]">
      {/* Hidden file input for the KG-upload triggers.
          Mounted once at the top of the tree so both the header button
          and the "My uploads" panel button can share it.
          D2 · SPD v5.76 (2026-07-12) — wrapped in `isAdmin` so the
          hidden input can't be programmatically clicked by non-admin
          users (belt-and-braces; the visible triggers already gate on
          isAdmin at click time). */}
      {isAdmin && (
        <input
          ref={kgUploadInputRef}
          type="file"
          multiple
          data-testid="atlas-kg-upload-input"
          style={{ display: 'none' }}
          onChange={(e) => handleKgUploadFiles(e.target.files)}
        />
      )}
      {/* Shell header — tenant selector + meta-query bar.
          Layout (D6 · 2026-02-07): row 1 holds Topic/Compare/Refresh/
          Concept-Finder/Memory/Intelligence/KG-Upload as a wrap-friendly
          horizontal cluster. Row 2 hosts the Meta-Query bar full-width
          so it doesn't get clipped by the ever-growing chip cluster. */}
      <div
        data-testid="atlas-explorer-shell-header"
        className="mx-auto flex flex-col gap-3 px-6 pt-6 pb-4 sm:px-12"
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Shell shield-check icon removed per D9 Cluster 6b ruling
              (2026-02-08) — decorative-only chrome with no info payload. */}
          <div className="relative group">
            <label
              htmlFor="atlas-tenant-select"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400/70 cursor-help"
            >
              Topic
            </label>
            <HoverTip
              testId="atlas-topic-label-hover-tag"
              align="bottom-left"
              title="Topic"
              description="A Topic is a high-level subject area made of Sub-Topics. Select which Topic you want to interrogate."
            />
          </div>
          <select
            id="atlas-tenant-select"
            data-testid="atlas-tenant-select"
            value={tenant || ''}
            onChange={(e) => { setTenant(e.target.value); setSelectedEntityId(null); }}
            className="rounded border border-cyan-400/40 bg-slate-900/60 px-3 py-1 text-sm text-cyan-100 focus:border-cyan-400 focus:outline-none"
          >
            {tenants.length === 0 && (
              <option value="">(no populated Topics yet)</option>
            )}
            {tenants.map((t) => (
              <option key={t} value={t}>{topicDisplayName(t)}</option>
            ))}
          </select>

          {/* Multi-Topic "Compare" dropdown — Mockup v0.6, label renamed
              from "Compare with" to "Compare" per D9 Cluster 3 follow-up
              B (2026-02-08). When set, the KA still renders only the
              Primary Topic's orbs (Iceberg blue). Sub-Topics also present
              in the Secondary Topic recolour to Amber (#fbbf24). */}
          <div className="relative group">
            <label
              htmlFor="atlas-compare-select"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80 cursor-help"
            >
              Compare
            </label>
            <HoverTip
              testId="atlas-compare-label-hover-tag"
              align="bottom-left"
              wide
              title="Compare"
              description="Overlay a second Topic to see where the two Topics share the same Sub-Topics and Concepts."
            />
          </div>
          <select
            id="atlas-compare-select"
            data-testid="atlas-compare-select"
            value={compareTenant || ''}
            onChange={(e) => setCompareTenant(e.target.value || null)}
            className="rounded border border-amber-400/40 bg-slate-900/60 px-3 py-1 text-sm text-amber-100 focus:border-amber-400 focus:outline-none"
          >
            <option value="">— none —</option>
            {tenants
              .filter((t) => t !== tenant)
              .map((t) => (
                <option key={t} value={t}>{topicDisplayName(t)}</option>
              ))}
          </select>
          {compareTenant && (
            <button
              type="button"
              data-testid="atlas-compare-clear-btn"
              onClick={() => setCompareTenant(null)}
              aria-label="Clear Secondary Topic overlay"
              className="rounded-full border border-amber-300/40 px-1.5 py-0.5 text-[10px] leading-none text-amber-100 hover:border-amber-200/80 hover:text-amber-50"
              title="Clear Secondary Topic overlay"
            >
              &#215;
            </button>
          )}
          <button
            type="button"
            data-testid="atlas-refresh-btn"
            onClick={() => reloadTenantState(tenant)}
            title="Refresh Topic state"
            className="flex items-center gap-1 rounded border border-cyan-400/30 bg-slate-900/40 px-2 py-1 text-[11px] text-cyan-200 transition-colors hover:border-cyan-400/60 hover:text-cyan-100"
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Refresh
          </button>

          <AtlasConceptFinder
            topic={tenant}
            onSelectConcept={(id) => {
              setSelectedEntityId(id);
              setView('entity');
            }}
            onSelectSubTopic={(entityType) => {
              setSubtopicFilter(entityType);
              setView('graph');
            }}
            onSelectTopic={(newTopic) => {
              if (newTopic !== tenant) {
                setTenant(newTopic);
                setSelectedEntityId(null);
                setSubtopicFilter(null);
              }
            }}
            onSearchStart={clearNewSince}
          />

          {/* Memory Economy badge hidden per operator directive (2026-02-10):
              the 5-part rich synopses legitimately expand concept_chars past
              raw_chars, so the char-delta "SAVED" framing is stale. The real
              CVP — session-persistent memory + no re-context cost — is now
              told through the AtlasOnboardingModal copy instead. Restore
              when the LLM cost-ledger wiring (P1 backlog · CVP Metrics pass)
              replaces the char-delta maths with real query-time cost avoided.
              Backend endpoint /api/knowledge-controls/memory-economy stays
              live so the KcLiveScoringPanel pill can keep rendering.
          <AtlasMemoryEconomyBadge topic={tenant} /> */}
          <AtlasIntelligenceBadge topic={tenant} />
        </div>
        {/* Meta-Query bar removed per D9 Cluster A ruling (2026-02-08):
            operator directive verbatim — "Delete this entirely. I don't
            recall ever asking for this either." Backend (agents/ka_m6.py
            + /api/ka/meta-query route + M6 pytest suite) preserved for
            potential future re-use; only the UI trigger is archived. */}
      </div>

      {/* View tabs (D12 · SPD v5.68, 2026-07-11): moved from a full-width
          row into a two-column strip that hugs just above the left-hand
          Sub-Topic panel. Tabs occupy the left cell (aligned with the
          380 px panel column); the Concepts/review-queue badges float in
          the right cell above the main content area. */}
      <div
        data-testid="atlas-view-tabs-strip"
        className="mx-auto grid px-6 sm:px-12"
        style={{ gridTemplateColumns: '380px 1fr', gap: 14, borderBottom: '1px solid rgba(34,211,238,0.20)' }}
      >
        <div data-testid="atlas-view-tabs" className="flex items-center gap-1">
          {VIEWS.map(({ key, label, Icon, tooltip }) => {
            const active = view === key;
            return (
              <div key={key} className="relative group">
                <button
                  type="button"
                  data-testid={`atlas-view-tab-${key}`}
                  onClick={() => {
                    // SPD v5.73 fix — every click on the Map tab bumps the
                    // reset token so AtlasScaleView returns to its Connection
                    // (bubbles) sub-view even when the top-level tab is
                    // already `graph`. Prior behaviour: click did nothing
                    // when already active, leaving the operator stuck on
                    // whatever sub-view they had drilled into.
                    if (key === 'graph') setMapResetToken((t) => t + 1);
                    setView(key);
                  }}
                  className={
                    'flex items-center gap-2 px-3 py-2 text-[12px] transition-colors ' +
                    (active
                      ? 'border-b-2 border-cyan-400 text-cyan-100'
                      : 'border-b-2 border-transparent text-cyan-300/60 hover:text-cyan-200')
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
                <HoverTip
                  testId={`atlas-view-tab-${key}-hover-tag`}
                  align="bottom-left"
                  title={label}
                  description={tooltip}
                />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-3 text-[11px] text-cyan-300/60">
          {/* SPD v5.73 (2026-02-11) — Removed the `{atlasEntities.length} Concepts`
              badge per operator D14 regression report. This badge was previously
              removed pre-fork; it re-appeared on a merge and needed to go
              again. The Sub-Topic Wiki (D12 drilled-in view) already surfaces
              per-Sub-Topic concept counts contextually. */}
          {reviewQueue.length > 0 && (
            <span data-testid="atlas-review-queue-count-badge">
              {reviewQueue.length} in review
            </span>
          )}
        </div>
      </div>

      {/* Body — either empty-state or the selected view */}
      <div className="relative mx-auto min-h-[54vh] px-6 py-6 sm:px-12">
        {error && (
          <div
            data-testid="atlas-error-banner"
            className="mb-4 flex items-center gap-2 rounded border border-red-400/50 bg-red-500/10 px-4 py-2 text-sm text-red-200"
          >
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}
        {/* SPD v5.90 — amber warning shown ONLY when there is no hard
            error. Distinguishes "one slice failed, graph still usable"
            from "everything is broken". */}
        {!error && partialWarning && (
          <div
            data-testid="atlas-partial-warning-banner"
            className="mb-4 flex items-center gap-2 rounded border border-amber-400/50 bg-amber-500/10 px-4 py-2 text-sm text-amber-200"
          >
            <AlertCircle className="h-4 w-4" />
            {partialWarning}
          </div>
        )}

        {/* Active sub-topic filter chip. Rendered when the operator has
            picked a Sub-Topic from the Concept Finder — narrows the
            Map view down to Concepts of that entity_type. */}
        {subtopicFilter && (
          <div
            data-testid="atlas-subtopic-filter-chip"
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-500/10 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-200"
          >
            <span className="font-mono opacity-70">Sub-Topic filter</span>
            <span className="font-medium">{subtopicFilter.replace(/_/g, ' ')}</span>
            <button
              type="button"
              data-testid="atlas-subtopic-filter-clear"
              onClick={() => setSubtopicFilter(null)}
              aria-label="Clear Sub-Topic filter"
              className="ml-1 rounded-full border border-amber-300/40 px-1.5 text-[10px] leading-none text-amber-100 hover:border-amber-200/80 hover:text-amber-50"
            >
              &#215;
            </button>
          </div>
        )}

        {isEmpty && !loading && (
          <EmptyState />
        )}

        {!isEmpty && view === 'graph' && (
          <AtlasScaleView
            mapResetToken={mapResetToken}
            entities={
              subtopicFilter
                ? atlasEntities.filter((e) => (e.entity_type || '').toLowerCase() === subtopicFilter.toLowerCase())
                : atlasEntities
            }
            documents={documents}
            newSince={newSince}
            tenant={tenant}
            sharedSubTopics={sharedSubTopics}
            compareTenant={compareTenant}
            onSelectConceptEntity={(id) => {
              setSelectedEntityId(id);
              setView('entity');
            }}
          />
        )}

        {!isEmpty && view === 'entity' && (
          <AtlasEntityView
            entity={selectedEntity}
            allEntities={atlasEntities}
            onSelect={setSelectedEntityId}
            sharedSubTopics={sharedSubTopics}
            compareTenant={compareTenant}
            secondarySubTopicCounts={secondarySubTopicCounts}
          />
        )}

        {/* What Changed (delta) tab removed per D9 Cluster 2 ruling
            (2026-02-08). AtlasDeltaFeed component preserved on disk. */}

        {view === 'documents' && (
          <AtlasDocuments tenant={tenant} newSince={newSince} />
        )}
      </div>

      {/* AuditVisibilityBanner removed per D9 Cluster 1 ruling (2026-02-08):
          cryptographic-audit surface is design-doc bleed-through, not
          user-facing chrome. Backend audit trail preserved intact. */}
    </div>
  );
});
KnowledgeAtlasExplorer.displayName = 'KnowledgeAtlasExplorer';


const EmptyState = () => (
  <div
    data-testid="atlas-empty-state"
    className="mx-auto mt-8 max-w-xl rounded-lg border border-cyan-400/30 bg-slate-900/50 p-8 text-center"
  >
    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/50">
      <Sparkles className="h-6 w-6 text-cyan-300" />
    </div>
    <p className="mx-auto max-w-md text-lg leading-relaxed text-cyan-200/70">
      (no populated Topics yet)
    </p>
  </div>
);

export default KnowledgeAtlasExplorer;
